import { CallerIdComponent } from './features/pos/caller-id/caller-id.component';
import { PrinterAlertDialogComponent } from './features/pos/printer-alerts/printer-alert-dialog.component';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ToastComponent } from './shared/components/notifications/toast/toast.component';
import { UpdateBannerComponent } from './shared/components/update-banner/update-banner.component';
import { AppUpdateService } from './core/services/app-update.service';
import { LanguageService } from './core/services/language.service';
import { OfflineBannerComponent } from './features/pos/shared/offline-banner/offline-banner.component';
import { OfflineSwitchComponent } from './features/pos/shared/offline-switch/offline-switch.component';
import { OfflineSessionGuardService } from './core/offline/offline-session-guard.service';
import { SatelliteFollowService } from './core/offline/satellite-follow.service';
import { ScheduledOrderAlertsService } from './features/pos/scheduled-orders/scheduled-order-alerts.service';
import { PrintFailedAlertsService } from './features/pos/shared/print-failed-alerts.service';
import { TableConnectPaymentAlertsService } from './features/pos/shared/tableconnect-payment-alerts.service';
import { PAYMENT_TERMINAL_ROUTE } from './core/offline/hub-surfaces';

/**
 * Root component for the POS terminal SPA. Thin shell — every route renders
 * into the single `<router-outlet />`. Two cross-cutting behaviours:
 *
 *   1. Language init: applies the operator's preferred language ASAP so
 *      subsequent renders see the correct Transloco active lang + RTL flip.
 *
 *   2. Offline visibility: the offline banner lives HERE, not in the POS
 *      shell. `pos-shell`, `order-screen`, `settle-screen`, `x-report`,
 *      `session-orders` and `pin-login` are SIBLING top-level routes, not
 *      children of the shell — so mounting the banner inside pos-shell
 *      (as it was) showed it on the floor plan and nowhere else. A cashier
 *      taking a payment or a manager reading an X-report had no indication
 *      the terminal was offline at all, which is what UAT reported.
 *      Mounting it at the root covers every current and future POS route
 *      instead of six copy-pastes the next screen will forget.
 *
 *   3. Stale-bundle recovery: when index.html has been served from a cache
 *      and the deployed app has shipped new lazy-chunk hashes, dynamic
 *      imports for route-level components return 404. Angular's Router
 *      emits a NavigationError; the user is stuck on the current screen and
 *      any subsequent navigation can fall through to the catch-all → /login.
 *      For POS terminals this is particularly bad: a cashier mid-shift would
 *      be bounced to the PIN screen. We detect chunk-load failures and force
 *      a hard reload so a fresh index.html (with the current chunk
 *      references) is fetched. After reload posGuard / posAuthGuard handle
 *      their own POS-aware redirects (e.g. /pos/s/:code) — never /login.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CallerIdComponent, PrinterAlertDialogComponent, CommonModule, RouterOutlet, ToastComponent, UpdateBannerComponent, OfflineBannerComponent, OfflineSwitchComponent],
  template: `
    <div class="app-chrome">
      <app-update-banner *ngIf="showUpdateBanner" />
      <app-offline-banner *ngIf="showOfflineBanner" />
    </div>
    <router-outlet />
    <!-- OF-SWITCH — confirmed offline / online switch (ConfirmBothWays). Same
         operator-route allow-list as the offline banner. -->
    <app-offline-switch *ngIf="showOfflineBanner" />
    <app-toast />
    <app-caller-id />
    <!-- PRNALERT-1 — printer not responding: needs an answer, on every POS operator screen. -->
    <app-printer-alert-dialog />
  `,
  styles: [`
    /* Groups the banner stack into one flex-none row so the routed screen
       takes exactly the remaining height — see OF-FIX-BANNER-2 in
       styles.scss. Collapses to 0px when no banner is rendered. */
    .app-chrome { display: block; }
  `],
})
export class AppComponent implements OnInit {
  private appUpdate = inject(AppUpdateService);
  private router = inject(Router);
  private language = inject(LanguageService);
  private satelliteFollow = inject(SatelliteFollowService);
  private scheduledOrderAlerts = inject(ScheduledOrderAlertsService);
  private printFailedAlerts = inject(PrintFailedAlertsService);
  private tableConnectPaymentAlerts = inject(TableConnectPaymentAlertsService);
  // OFFLINE-AUTH-401 — retire a hub-minted session on reconnect, before
  // the cloud discovers it with a burst of 401s.
  private offlineSessionGuard = inject(OfflineSessionGuardService);

  /**
   * Surface-aware visibility for the version-update banner.
   *
   * Showing the banner on customer-facing surfaces (kiosk attractor,
   * pickup-display counter board) is wrong on two counts: no operator
   * is sitting there to click "Refresh Now", and a red "new version"
   * banner pasted across a customer screen looks like an error.
   * Those two surfaces are unattended displays — the right behaviour
   * is to suppress the banner and let the next natural refresh (the
   * pickup-display polls every few seconds; the kiosk attractor
   * eventually times out and reloads itself) pick up the new bundle.
   *
   * POS terminal + KDS + the login / select-store / register flows
   * all DO show the banner — they're operator-attended, the operator
   * can pick a quiet moment to refresh, and the prompt is honest about
   * what's happening.
   */
  showUpdateBanner = !isCustomerFacingRoute(this.router.url);

  /**
   * Offline-banner visibility. Same route-awareness as the update banner
   * but a tighter allow-list: only surfaces that HAVE an offline story
   * behind them.
   *
   * Kiosk / KDS / pickup-display / customer-display are excluded for the
   * same reason `hubBaseUrlInterceptor` excludes them (OF-FIX-C7) — they
   * have no hub mirror, so an offline banner there would announce a mode
   * they cannot enter. The kiosk especially is unattended and takes
   * payment; a red banner with no staff to read it buys nothing.
   */
  showOfflineBanner = isPosOperatorRoute(this.router.url);

  ngOnInit(): void {
    this.language.initialiseFromAuthOrStorage();
    this.appUpdate.start();
    // RECON-6 — listen for the hub's all-clear so this terminal follows
    // it back online instead of deciding from its own heartbeat. Started
    // at the root because the hub socket outlives any single screen.
    this.satelliteFollow.start();
    this.offlineSessionGuard.start();
    // SO-2 — scheduled-order release / failure / missed chimes. Root-level for the
    // same reason: the POS shell is not mounted on the order or settle screens.
    this.scheduledOrderAlerts.start();
    // OFP-093 — the offline hub's "printed nowhere" frame (a KOT, receipt,
    // bill or drawer kick). Root-level for the same reason as SO-2: a KOT or
    // a receipt usually fails while the order or settle screen is up.
    this.printFailedAlerts.start();
    // TCPAY-1 — a guest's pay-at-table payment failed, or was taken and not
    // recorded. Root-level for the same reason: it used to be the shell's
    // toast, unseen on the order and settle screens.
    this.tableConnectPaymentAlerts.start();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(evt => {
        const url = evt.urlAfterRedirects ?? evt.url ?? '';
        this.showUpdateBanner = !isCustomerFacingRoute(url);
        this.showOfflineBanner = isPosOperatorRoute(url);
      });

    this.router.events.subscribe(evt => {
      if (!(evt instanceof NavigationError)) return;
      if (!isChunkLoadError(evt.error)) return;

      console.warn('[AppComponent] Lazy bundle failed to load — forcing reload to recover.', {
        url: evt.url,
        error: (evt.error as { message?: string })?.message,
      });

      try {
        window.location.assign(evt.url || window.location.pathname);
      } catch {
        window.location.reload();
      }
    });
  }

}

/**
 * Customer-facing routes — the kiosk attractor / menu / cart and the
 * pickup-display counter board. No operator is at the screen to act
 * on chrome like a version-update banner.
 *
 * Matches both the storeCode-keyed entry URLs (`/kiosk/s/<code>`,
 * `/pickup-display/s/<code>`) AND any sub-route the kiosk shell
 * navigates the customer through. The `/kds-unavailable` placeholder
 * route is operator-facing (a chef whose KDS launch failed) so it
 * does NOT match here.
 */
function isCustomerFacingRoute(url: string): boolean {
  if (!url) return false;
  // Strip query string + fragment to match cleanly on the path.
  const path = url.split('?')[0].split('#')[0];
  return path.startsWith('/kiosk/') || path.startsWith('/pickup-display/');
}

/**
 * Operator-attended POS routes — everything the offline banner should
 * appear on.
 *
 * Deliberately an ALLOW-list: a new customer-facing surface added later
 * gets no banner by default, which is the safe direction. `/pos` covers
 * the PIN screen, the terminal, order, settle, x-report, reports, queue
 * and session-orders; `/login`, `/select-store` and `/edge-clients` are
 * the terminal-bring-up flow, where knowing the WAN is down is exactly
 * what tells the installer why registration is failing.
 *
 * PTM-7 — minus the Payment Terminal (`/pos/pt/…` working screens and the
 * `/pos/s/<code>/pt` PIN entry): online-only with no hub (D7), it shows its
 * own offline state, and this banner's hub wording does not apply to it.
 * The route test is shared with the hub rules (core/offline/hub-surfaces).
 */
function isPosOperatorRoute(url: string): boolean {
  if (!url) return false;
  const path = url.split('?')[0].split('#')[0];
  if (PAYMENT_TERMINAL_ROUTE.test(path)) return false;
  return (
    path === '/pos' ||
    path.startsWith('/pos/') ||
    path === '/login' ||
    path.startsWith('/login?') ||
    path.startsWith('/select-store') ||
    path.startsWith('/edge-clients/')
  );
}

/**
 * Heuristic check for the various ways browsers / bundlers surface a failed
 * dynamic-import: native `ChunkLoadError`, the Webpack-style "Loading chunk N
 * failed" message, the modern Vite/esbuild "Failed to fetch dynamically
 * imported module" message, or a plain 404 on a `.js` URL.
 */
function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: string; message?: string };
  const name = (e.name || '').toString();
  const msg = (e.message || '').toString();
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}
